import { SquadPlayer } from '../types';
import { CurrentClub, Player, PlayerMinimized } from '../api-types';
import { useSnackbar } from 'notistack';
import { useStringsContext } from '../../../../contexts/strings';
import { useCallback } from 'react';
import { useUserAuth } from '../../../../contexts/auth';
import adminApi from '../../../../api/admin-api';
import { toCurrentClubEditBody } from '../current-club-edit';

export const createSquadPlayer = (teamId: string, player: Player): SquadPlayer => {
  return {
    ...player,
    get activeCurrentClub() {
      let activeCurrentClub: CurrentClub | null = null;
      if (Array.isArray(this.currentClub)) {
        activeCurrentClub = this.currentClub.find((club) => club.id === teamId) || null;
        // if (!activeCurrentClub) {
        //  console.error('Current club exists but no active club found!', this);
        // }
      }
      return activeCurrentClub || {};
    },
  };
};

export const usePlayer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { strings } = useStringsContext();
  const { currentUser } = useUserAuth();

  const updateCurrentClub = useCallback(
    async (player: SquadPlayer, updatedCurrentClub: CurrentClub, teamId: string) => {
      try {
        const response = await adminApi.put({
          path: `/player/currentClub/edit/${player.fogisId}`,
          body: toCurrentClubEditBody(updatedCurrentClub, teamId),
          auth: await currentUser?.getIdToken(),
        });
        if (!response.ok) {
          throw new Error(`Update current club failed! (${response.status})`);
        }
      } catch (error: any) {
        console.error(error);

        enqueueSnackbar(strings.messages.saveError, {
          variant: 'error',
        });
        throw error;
      }
    },
    [enqueueSnackbar, strings, currentUser]
  );

  const updatePlayer = useCallback(
    async (updatedPlayer: SquadPlayer) => {
      try {
        await adminApi.put({
          path: `/player/edit`,
          body: updatedPlayer,
          auth: await currentUser?.getIdToken(),
        });

        enqueueSnackbar(strings.messages.saveSuccess, {
          variant: 'success',
        });
      } catch (error: any) {
        console.error(error);

        enqueueSnackbar(strings.messages.saveError, {
          variant: 'error',
        });
        throw error;
      }
    },
    [enqueueSnackbar, strings, currentUser]
  );

  const removePlayer = useCallback(
    async (teamId: string, season: number, player: SquadPlayer) => {
      try {
        const response = await adminApi.put({
          path: '/player/remove',
          body: {
            playerId: player.fogisId,
            teamId: teamId,
            year: season,
          },
          auth: await currentUser?.getIdToken(),
        });
        if (!response.ok) {
          throw new Error(`Remove player failed! (${response.status})`);
        }

        enqueueSnackbar(strings.messages.deleteSuccess, {
          variant: 'success',
        });
      } catch (error: any) {
        console.error(error);

        enqueueSnackbar(strings.messages.deleteError, {
          variant: 'error',
        });
        throw error;
      }
    },
    [enqueueSnackbar, strings, currentUser]
  );

  const assignPlayers = useCallback(
    async (teamId: string, season: number, players: PlayerMinimized[]) => {
      try {
        for (const player of players) {
          const response = await adminApi.put({
            path: '/player/assign',
            body: {
              playerId: player.fogisId,
              teamId: teamId,
              year: season,
            },
            auth: await currentUser?.getIdToken(),
          });

          if (!response.ok) {
            throw new Error(`Assign player failed! (${response.status})`);
          }
        }

        enqueueSnackbar(strings.messages.saveSuccess, {
          variant: 'success',
        });
      } catch (error: any) {
        console.error(error);

        enqueueSnackbar(strings.messages.saveError, {
          variant: 'error',
        });
        throw error;
      }
    },
    [enqueueSnackbar, strings, currentUser]
  );

  const fetchPlayer = useCallback(
    async (teamId: string, playerId: number): Promise<SquadPlayer | null> => {
      let squadPlayer: SquadPlayer | null = null;

      try {
        const response = await adminApi.get({
          path: `/player/${playerId}/connectedleague`,
          auth: await currentUser?.getIdToken(),
        });

        if (!response.ok) {
          throw new Error(`Fetching player failed! (${response.status})`);
        }

        const data: Player = await response.json();
        squadPlayer = createSquadPlayer(teamId, data);
      } catch (error) {
        console.error(error);

        enqueueSnackbar(strings.messages.loadingError, {
          variant: 'error',
        });
        throw error;
      }

      return squadPlayer;
    },
    [enqueueSnackbar, strings, currentUser]
  );

  const uploadPlayerImage = useCallback(
    async (player: Player, file: File, year: string) => {
      try {
        const formData = new FormData();
        formData.append('image', file);

        const response = await adminApi.put({
          path: `/player/image/edit/${player.fogisId}/${year}`,
          formData,
          auth: await currentUser?.getIdToken(),
        });

        if (!response.ok) {
          throw new Error(`Upload player image failed! (${response.status})`);
        }

        enqueueSnackbar(strings.messages.uploadSuccess, {
          variant: 'success',
        });
      } catch (error: any) {
        console.error(error);

        enqueueSnackbar(strings.messages.uploadError, {
          variant: 'error',
        });
        throw error;
      }
    },
    [enqueueSnackbar, strings, currentUser]
  );

  return {
    updatePlayer,
    removePlayer,
    updateCurrentClub,
    assignPlayers,
    fetchPlayer,
    uploadPlayerImage,
  };
};
